import { Banner, Button, Form, TextField, TextStyle } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CSVDownloadButton } from "../components/CSVDownloadButton.jsx"
import { CSVExportSuggestion } from "../components/CSVExportSuggestion.jsx"
import Logo from "../components/Logo"
import { TopMenu } from "../components/TopMenu.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider"
import SingleProductImportModal from "../components/single/SingleProductImportModal"
import { SupportedPlatformsIcons } from "../components/single/SupportedPlatformsIcons.jsx"
import UploadSingleProduct from "../components/single/UploadSingleProduct"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch"
import { pushCrispEvent } from "../utils/crisp.js"
import { validateUrl } from "../utils/validateUrl"
import { BackgroundJobBanner } from "../components/BackgroundJobBanner.jsx"
import { BackgroundJobsStateContext } from "../components/providers/BackgroundJobsStateProvider.jsx"
import { KopyPlusCTA } from "../components/ai/KopyPlusCTA.jsx"
import { usePostHog } from "posthog-js/react"
import { useStateRef } from "../hooks/useStateRef.js"
import { CRXSuggestionInfoBanner } from "../components/CRXSuggestionInfoBanner.jsx"
import { CRXSuggestionErrorBanner } from "../components/CRXSuggestionErrorBanner.jsx"
import { CRXSettingsModal } from "../components/CRXSettings.jsx"

const Single = () => {
	const posthog = usePostHog()
	const { state } = useContext(GlobalStateContext)
	const { backgroundJobsState } = useContext(BackgroundJobsStateContext)
	const authenticatedFetch = useAuthenticatedFetch()
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [productUrl, setProductUrl] = useState("")
	const [isUrlValid, setIsUrlValid] = useState(true)
	const [loading, setLoading, loadingRef] = useStateRef(false)
	const [fetchError, setFetchError] = useState("")
	const [fetchedProduct, setFetchedProduct] = useState(null)
	const [fetchedProductCurrency, setFetchedProductCurrency] = useState("USD")

	const [modalIsOpen, setModalIsOpen] = useState(false)
	const [importInput, setImportInput] = useState(null)
	const [readyToUpload, setReadyToUpload] = useState(false)

	const [csvDownloadUrl, setCsvDownloadUrl] = useState("")
	const [csvFilename, setCsvFilename] = useState("")

	const [failedImporting, setFailedImporting] = useState(false)
	const [readyToShowAdBanner, setReadyToShowAdBanner] = useState(false)

	const [showCrxSuggestionInfoBanner, setShowCrxSuggestionInfoBanner] =
		useState(false)
	const [showCrxSuggestionErrorBanner, setShowCrxSuggestionErrorBanner] =
		useState(false)
	const [CRXModalIsOpen, setCRXModalIsOpen] = useState(false)

	const [showBackgroundJobBanner, setShowBackgroundJobBanner] =
		useState(false)

	const changeProductURL = (value) => {
		const validUrl = validateUrl(value)
		if (validUrl) {
			setProductUrl(value)
			setIsUrlValid(true)
		} else {
			setProductUrl(value)
			setIsUrlValid(false)
		}
	}

	const getProduct = (url) => {
		const isCrxScrapableProduct = // Temporary. Remove after all platforms scrapers have been implemented into Kopy Chrome extension
			url.includes("aliexpress.com") ||
			url.includes("aliexpress.us") ||
			url.includes("aliexpress.ru") ||
			url.includes("shein.com")
		setLoading(true)
		setTimeout(() => {
			if (loadingRef.current === true && isCrxScrapableProduct) {
				setShowCrxSuggestionInfoBanner(true)
			}
		}, 5000)
		setFetchError("")
		setModalIsOpen(false)
		setFetchedProduct(null)
		setFetchedProductCurrency("USD")
		setCsvDownloadUrl("")
		setCsvFilename("")
		setFailedImporting(false)
		setShowBackgroundJobBanner(false)
		setShowCrxSuggestionErrorBanner(false)

		pushCrispEvent("single_product_fetch_submit", { productUrl: url })
		posthog?.capture("single_product_import_clicked")
		authenticatedFetch("/api/productFetcher/getProduct", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ productUrl: validateUrl(url) }),
		})
			.then((res) => res.json())
			.then((res) => {
				if (res.scrapingFailed) {
					if (isCrxScrapableProduct) {
						setShowCrxSuggestionErrorBanner(true)
					}
				}
				if (res.error) {
					setLoading(false)
					setFetchError(res.error)
					setShowCrxSuggestionInfoBanner(false)
				} else {
					setLoading(false)
					setShowCrxSuggestionInfoBanner(false)
					if (res.product) {
						setFetchedProductCurrency(res.currency || "USD")
						setFetchedProduct(res.product)
						setModalIsOpen(true)
					}
					if (res.callbackId) {
						setShowBackgroundJobBanner(true)
					}
				}
			})
			.catch((err) => {
				console.error("getProduct catched error", err)
				setLoading(false)
				setFetchError(t("single.fetch_error"))
				setShowCrxSuggestionInfoBanner(false)
			})
	}

	const submitProductToUpload = (productToSubmit, aiOrders) => {
		pushCrispEvent("single_product_import_submit", {
			productUrl: productUrl,
		})
		setTimeout(() => {
			setImportInput({ product: productToSubmit, aiOrders })
			setModalIsOpen(false)
			setReadyToUpload(true)
			setLoading(true)
			setReadyToShowAdBanner(true)
		}, 500)
	}

	const onFinishedUploading = async (productId) => {
		setReadyToUpload(false)
		setLoading(false)
	}

	const onCsvExport = (csvUrl, filename) => {
		setModalIsOpen(false)
		setCsvDownloadUrl(csvUrl)
		setCsvFilename(filename)
	}

	useEffect(() => {
		const importUrl = searchParams.get("importUrl")

		if (!importUrl) return

		setProductUrl(importUrl)
		getProduct(importUrl)

		setTimeout(() => {
			searchParams.delete("importUrl")
			setSearchParams(searchParams)
		}, 1000)
	}, [])

	useEffect(async () => {
		const backgroundJobs = backgroundJobsState.jobs

		if (searchParams.get("productHash") && backgroundJobs.length > 0) {
			const productHash = searchParams.get("productHash")
			const job = backgroundJobs.find((job) => job.id === productHash)

			if (job.product) {
				setFetchedProduct(job.product)
				setFetchedProductCurrency(job.currency)
				setModalIsOpen(true)

				searchParams.delete("productHash")
				setSearchParams(searchParams)
			}
		}
		if (searchParams.get("resultId")) {
			const resultId = searchParams.get("resultId")
			const result = await authenticatedFetch(
				`/api/crx/getCrxProductScrapingResult?resultId=${resultId}`,
			)
				.then((res) => res.json())
				.catch((err) => {
					console.error(
						"getCrxProductScrapingResult catched error",
						err,
					)
					return null
				})
			if (result) {
				setFetchedProduct(result.product)
				setFetchedProductCurrency(result.currency)
				setProductUrl(result.productUrl)
				setModalIsOpen(true)

				searchParams.delete("resultId")
				setSearchParams(searchParams)
			}
		}
	}, [searchParams])

	useEffect(() => {
		if (!state.hasValidKopyBasicSubscription) {
			navigate("/upgradebasic")
		}
	}, [])

	return (
		<div>
			<TopMenu goBack="/" storeSelector storeSelectorDisabled={loading} />

			<div
				style={{
					paddingTop: "3.5rem",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Logo />

				<div style={{ maxWidth: "40rem", width: "100%" }}>
					<div style={{ marginTop: "3rem" }}>
						<Form
							onSubmit={
								!loading && isUrlValid
									? () => getProduct(productUrl)
									: () => void 0
							}
						>
							<div
								style={{
									display: "flex",
									gap: "0.5rem",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<span style={{ fontSize: "0.85rem" }}>
									{t("single.product_url")}
								</span>
								<SupportedPlatformsIcons />
							</div>

							<div style={{ marginTop: "0.5rem" }}>
								<TextField
									inputMode="url"
									clearButton={!loading}
									onClearButtonClick={() =>
										changeProductURL("")
									}
									placeholder="[store].com/[product]"
									value={productUrl}
									onChange={changeProductURL}
									connectedRight={
										<Button
											onClick={() =>
												getProduct(productUrl)
											}
											loading={loading}
											disabled={
												loading ||
												!isUrlValid ||
												productUrl === ""
											}
										>
											{t("single.import_product")}
										</Button>
									}
								/>
							</div>
							{!isUrlValid ? (
								<div style={{ marginTop: "1rem" }}>
									<TextStyle variation="negative">
										{t("single.url_is_invalid")}
									</TextStyle>
								</div>
							) : (
								""
							)}
						</Form>

						{fetchError &&
						fetchError !== "" &&
						!showCrxSuggestionErrorBanner ? (
							<div style={{ marginTop: "1rem" }}>
								<Banner status="critical">
									{fetchError ===
									"is_temu_collection_or_store_url"
										? t(
												"single.temu_collection_or_store_url",
											)
										: t("single.fetch_error_display")}
								</Banner>
							</div>
						) : null}

						{showBackgroundJobBanner ? (
							<BackgroundJobBanner />
						) : null}

						{failedImporting && <CSVExportSuggestion />}

						{showCrxSuggestionInfoBanner && (
							<CRXSuggestionInfoBanner
								openModal={() => setCRXModalIsOpen(true)}
							/>
						)}

						{showCrxSuggestionErrorBanner && (
							<CRXSuggestionErrorBanner
								openModal={() => setCRXModalIsOpen(true)}
							/>
						)}

						<CRXSettingsModal
							open={CRXModalIsOpen}
							setOpen={setCRXModalIsOpen}
						/>

						<CSVDownloadButton
							csvDownloadUrl={csvDownloadUrl}
							csvFilename={csvFilename}
						/>

						<UploadSingleProduct
							input={importInput}
							productUrl={productUrl}
							readyToUpload={readyToUpload}
							onFinishedUploading={onFinishedUploading}
							onFailedUploading={() => setFailedImporting(true)}
						/>

						{readyToShowAdBanner &&
							!state.hasValidKopyPlusSubscription && (
								<div style={{ marginTop: "1rem" }}>
									<KopyPlusCTA
										overrideWidth="fit-content"
										context="single"
									/>
								</div>
							)}
					</div>
				</div>
			</div>

			{modalIsOpen && (
				<SingleProductImportModal
					modalIsOpen={modalIsOpen}
					product={fetchedProduct}
					productCurrency={fetchedProductCurrency}
					productUrl={productUrl}
					shopCurrency={state.shopCurrency}
					submit={submitProductToUpload}
					onModalClose={() => setModalIsOpen(false)}
					onCsvExport={onCsvExport}
				/>
			)}
		</div>
	)
}

export default Single
